import { createBrowserRouter, Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import ProtectedRoute from '@/components/auth/ProtectedRoute';
import PublicRoute from '@/components/auth/PublicRoute';
import ErrorPage from '@/pages/ErrorPage';
import NotFoundPage from '@/pages/NotFoundPage';

// Lazy load pages for code splitting
const LandingPage1 = lazy(() => import('@/pages/LandingPage1'));
const GroupTrialPage = lazy(() => import('@/pages/GroupTrialPage'));
const PurchasePage = lazy(() => import('@/pages/PurchasePage'));
const Purchase47Page = lazy(() => import('@/pages/Purchase47Page'));
const PaymentThankYouPage = lazy(() => import('@/pages/PaymentThankYouPage'));
const LoginPage = lazy(() => import('@/pages/LoginPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/pages/ResetPasswordPage'));
const PasswordCreationPage = lazy(() => import('@/pages/PasswordCreationPage'));
const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const CourseLessonsPage = lazy(() => import('@/pages/CourseLessonsPage'));
const CourseContentPage = lazy(() => import('@/pages/CourseContentPage'));
const PaymentSuccessPage = lazy(() => import('@/pages/PaymentSuccessPage'));
const PaymentCancelPage = lazy(() => import('@/pages/PaymentCancelPage'));
const PaymentErrorPage = lazy(() => import('@/pages/PaymentErrorPage'));
const TrialDashboardPage = lazy(() => import('@/pages/TrialDashboardPage'));
const WebinarThankYouPage = lazy(() => import('@/pages/WebinarThankYouPage'));
const WebinarWatchRoomPage = lazy(() => import('@/pages/WebinarWatchRoomPage'));
const WebinarReplayPage = lazy(() => import('@/pages/WebinarReplayPage'));
const WebinarRegistrationPage = lazy(() => import('@/pages/WebinarRegistrationPage'));

// Wrapper component for lazy loaded routes
const LazyRoute = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<LoadingSpinner />}>{children}</Suspense>
);

export const router = createBrowserRouter([
  {
    path: '/',
    errorElement: <ErrorPage />,
    children: [
      {
        index: true,
        element: <Navigate to="/dashboard" replace />,
      },
      {
        path: 'landing-page-1',
        element: (
          <LazyRoute>
            <PublicRoute>
              <LandingPage1 />
            </PublicRoute>
          </LazyRoute>
        ),
      },
      {
        path: 'group-trial',
        element: (
          <LazyRoute>
            <PublicRoute>
              <GroupTrialPage />
            </PublicRoute>
          </LazyRoute>
        ),
      },
      {
        path: 'purchase',
        element: (
          <LazyRoute>
            <PublicRoute>
              <PurchasePage />
            </PublicRoute>
          </LazyRoute>
        ),
      },
      {
        path: 'purchase-47',
        element: (
          <LazyRoute>
            <PublicRoute>
              <Purchase47Page />
            </PublicRoute>
          </LazyRoute>
        ),
      },
      {
        path: 'payment/success',
        element: (
          <LazyRoute>
            <PublicRoute>
              <PaymentSuccessPage />
            </PublicRoute>
          </LazyRoute>
        ),
      },
      {
        path: 'payment/thank-you',
        element: (
          <LazyRoute>
            <PublicRoute>
              <PaymentThankYouPage />
            </PublicRoute>
          </LazyRoute>
        ),
      },
      {
        path: 'payment/cancel',
        element: (
          <LazyRoute>
            <PublicRoute>
              <PaymentCancelPage />
            </PublicRoute>
          </LazyRoute>
        ),
      },
      {
        path: 'payment/error',
        element: (
          <LazyRoute>
            <PublicRoute>
              <PaymentErrorPage />
            </PublicRoute>
          </LazyRoute>
        ),
      },
      {
        path: 'login',
        element: (
          <LazyRoute>
            <PublicRoute redirectIfAuthenticated>
              <LoginPage />
            </PublicRoute>
          </LazyRoute>
        ),
      },
      {
        path: 'forgot-password',
        element: (
          <LazyRoute>
            <PublicRoute>
              <ForgotPasswordPage />
            </PublicRoute>
          </LazyRoute>
        ),
      },
      {
        path: 'reset-password',
        element: (
          <LazyRoute>
            <PublicRoute>
              <ResetPasswordPage />
            </PublicRoute>
          </LazyRoute>
        ),
      },
      {
        path: 'create-password',
        element: (
          <LazyRoute>
            <PublicRoute>
              <PasswordCreationPage />
            </PublicRoute>
          </LazyRoute>
        ),
      },
      {
        path: 'dashboard',
        element: (
          <LazyRoute>
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          </LazyRoute>
        ),
      },
      {
        path: 'trial-dashboard',
        element: (
          <LazyRoute>
            <PublicRoute>
              <TrialDashboardPage />
            </PublicRoute>
          </LazyRoute>
        ),
      },
      {
        path: 'course',
        element: (
          <LazyRoute>
            <ProtectedRoute>
              <CourseLessonsPage />
            </ProtectedRoute>
          </LazyRoute>
        ),
      },
      {
        path: 'course/lesson/:lessonId',
        element: (
          <LazyRoute>
            <ProtectedRoute>
              <CourseContentPage />
            </ProtectedRoute>
          </LazyRoute>
        ),
      },
      {
        path: 'webinar-thank-you',
        element: (
          <LazyRoute>
            <PublicRoute>
              <WebinarThankYouPage />
            </PublicRoute>
          </LazyRoute>
        ),
      },
      {
        path: 'webinar-watch-room',
        element: (
          <LazyRoute>
            <PublicRoute>
              <WebinarWatchRoomPage />
            </PublicRoute>
          </LazyRoute>
        ),
      },
      {
        path: 'webinar-replay',
        element: (
          <LazyRoute>
            <PublicRoute>
              <WebinarReplayPage />
            </PublicRoute>
          </LazyRoute>
        ),
      },
      {
        path: 'webinar-registration',
        element: (
          <LazyRoute>
            <PublicRoute>
              <WebinarRegistrationPage />
            </PublicRoute>
          </LazyRoute>
        ),
      },
      {
        path: '404',
        element: <NotFoundPage />,
      },
      {
        path: '*',
        element: <NotFoundPage />,
      },
    ],
  },
]);
